import React, { useRef, useEffect } from 'react';
import cn from 'classnames';
// @ts-ignore
import Glide from '@glidejs/glide';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Carousel, { CarouselItem } from 'tbk-components/src/components/Carousel';
import Video, { IVideoProps } from '../Video';

export interface IStory {
	image?: IImage;
	video?: IVideoProps;
	text?: string;
	name?: string;
}

export interface IMultiStoryProps extends IBaseElementProps {
	subheading?: string;
	heading?: string;
	stories?: IStory[];
}

/**
 * Multi Story
 * @block
 * @requiresHooks
 * @icon images-alt2
 */
const MultiStory: React.FC<IMultiStoryProps> = ({
	className,
	classNames = [],
	subheading,
	heading,
	stories = [],
}) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		let isMounted = false;

		if (!ref.current) {
			return;
		}

		const carousel = ref.current.querySelector(
			'.multi-story-glide',
		) as HTMLDivElement;

		let playButtons = ref.current?.querySelectorAll(
			'.glide__slide--clone .btn-play',
		) as NodeListOf<HTMLInputElement>;

		const handleClick = (event: any) => {
			const target = event.target as HTMLInputElement;
			target.style.display = 'none';

			(target?.parentNode as HTMLDivElement).style.backgroundImage = null;

			(
				target?.parentNode?.firstElementChild
					?.firstElementChild as HTMLElement
			).style.display = 'block';
		};

		if (!carousel) {
			return;
		}

		const glideCarousel = new Glide(carousel, {
			type: 'carousel',
			focusAt: 'center',
			perView: 3,
			gap: 25,
			peek: {
				before: 100,
				after: 100,
			},
			breakpoints: {
				768: {
					perView: 1,
				},
				576: {
					perView: 1,
					peek: {
						before: 50,
						after: 50,
					},
				},
			},
		});

		// Resize height
		const glideHandleHeight = function () {
			const activeSlide = ref.current.querySelector(
				'.glide__slide--active',
			) as HTMLDivElement;
			const activeSlideHeight = activeSlide
				? activeSlide.offsetHeight
				: 0;

			const glideTrack = ref.current.querySelector(
				'.glide__track',
			) as HTMLDivElement;
			const glideTrackHeight = glideTrack ? glideTrack.offsetHeight : 0;

			if (activeSlideHeight !== glideTrackHeight) {
				glideTrack.style.height = `${activeSlideHeight}px`;
			}
		};

		const glideHandleCloneEvents = function () {
			playButtons = ref.current?.querySelectorAll(
				'.glide__slide--clone .btn-play',
			) as NodeListOf<HTMLInputElement>;

			playButtons &&
				playButtons.forEach((playButton) => {
					playButton.addEventListener('focus', handleClick);
				});
		};

		glideCarousel.on(
			['build.after', 'run.after', 'mount.after'],
			glideHandleHeight,
		);

		// Add clone events on Carousel build
		glideCarousel.on(['build.after'], glideHandleCloneEvents);

		// Mount as soon as body receives 'rendered' class
		const observer = new MutationObserver(() => {
			if (document.body.classList.contains('rendered') && !isMounted) {
				isMounted = true;
				glideCarousel.mount();
			}
		});

		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['class'],
		});

		// Or when page fully loads
		const handlePageLoad = () => {
			if (!isMounted) {
				isMounted = true;
				glideCarousel.mount();
			}
		};
		window.addEventListener('load', handlePageLoad);

		return () => {
			playButtons &&
				playButtons.forEach((playButton) => {
					playButton.removeEventListener('focus', handleClick);
				});

			window.removeEventListener('load', handlePageLoad);
			isMounted && glideCarousel.destroy();
			observer.disconnect();
		};
	}, []);

	return (
		<div
			className={className || cn('multi-story', ...classNames)}
			ref={ref}
		>
			{subheading && (
				<p className="multi-story__subheading">{subheading}</p>
			)}
			{heading && <h2 className="multi-story__heading">{heading}</h2>}
			<div className="multi-story-glide glide carousel carousel--items-5">
				<div className="glide__track" data-glide-el="track">
					<ul className="glide__slides">
						{stories.map(({ image, video, text, name }, index) => (
							<CarouselItem key={index}>
								<div className="multi-story-item">
									<div className="multi-story-media-wrapper">
										{video && <Video {...video} />}
										{!video && image && (
											<Image {...image} />
										)}
									</div>
									{text && (
										<div className="multi-story-text">
											{text}
										</div>
									)}
									{name && (
										<div className="multi-story-name">
											{name}
										</div>
									)}
								</div>
							</CarouselItem>
						))}
					</ul>
				</div>
				<div className="glide__arrows" data-glide-el="controls">
					<button
						className="btn btn-default glide__arrow glide__arrow--left"
						data-glide-dir="<"
						aria-label="Previous"
					>
						<i className="icon icon-chevron-left" />
					</button>
					<button
						className="btn btn-default glide__arrow glide__arrow--right"
						data-glide-dir=">"
						aria-label="Next"
					>
						<i className="icon icon-chevron-right" />
					</button>
				</div>
			</div>
		</div>
	);
};

export default MultiStory;
